import { FC, useMemo, lazy, Suspense } from "react";

import {
  CriteriaCheckSettings,
  MakeDecisionRequirements
} from "types/application";
const ScoreGraph = lazy(
  () =>
    import("views/applications-id/components/ApplicationScoreCard/ScoreGraph")
);
import { ScoreGraphSize } from "./constants";
import { ScreeningCriteriaType } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import {
  ApplicationStatus,
  DecisionAction,
  RequiresOverridesIdentityVerification
} from "shared/types/application";
import { Card } from "shared/components/Card";
import { useAuthContext } from "providers/Auth";
import { RecommendationButton } from "../RecommendationButton";
import { toFixedFloored } from "utils/toFixedFloored";
import {
  AnyApplicant,
  ApplicationActions
} from "shared/utils/application-actions";
import { Loader } from "shared/components/Loader";
type Props = {
  applicationScore?: number | null;
  recommendation?: DecisionAction;
  onButtonClick?: () => void;
  criteriaCheckSettings?: CriteriaCheckSettings;
  applicationSubmitted: boolean;
  templateCriteriaSettings?: ScreeningCriteriaType;
  status?: ApplicationStatus;
  applicants?: AnyApplicant[];
  requires_overrides_identity_verification?: RequiresOverridesIdentityVerification;
  requirements?: MakeDecisionRequirements[];
};

export const ApplicationScoreCard: FC<Props> = ({
  applicationScore,
  recommendation,
  onButtonClick,
  applicationSubmitted,
  templateCriteriaSettings,
  status,
  applicants,
  requires_overrides_identity_verification,
  requirements
}) => {
  const { permissions, type } = useAuthContext();

  const { decline_range_end, conditional_approval_range_end } =
    templateCriteriaSettings || {};

  let score: string | number = 0;
  if (applicationScore)
    score = applicationScore === 10 ? 10 : toFixedFloored(applicationScore, 1);

  let recommendationText = " conditional approval";
  if (recommendation === DecisionAction.Approve)
    recommendationText = " approval";
  else if (recommendation === DecisionAction.Decline)
    recommendationText = " declining";

  const canMakeDecision = useMemo(() => {
    const { hidden, disabled } = new ApplicationActions({
      status: status || ApplicationStatus.Started,
      permissions,
      applicants: applicants || [],
      requirements
    }).makeDecision;
    return !hidden && !disabled;
  }, [
    requires_overrides_identity_verification,
    applicants,
    status,
    type,
    permissions,
    requirements
  ]);

  return (
    <Card
      // Flex column is needed for button
      className="w-[300px] min-h-[274px] p-6 flex flex-col h-full"
      onClick={
        applicationSubmitted && !recommendation ? onButtonClick : undefined
      }
    >
      <div className="flex h-[136px] justify-center items-center mt-8">
        <Suspense fallback={<Loader />}>
          <ScoreGraph
            size={ScoreGraphSize.LG}
            score={applicationScore}
            recommendation={recommendation}
            declineRangeEnd={decline_range_end}
            conditionalApprovalRangeEnd={conditional_approval_range_end}
          />
        </Suspense>
      </div>

      <p className="mt-auto text-center text-xs text-content-300 font-medium pt-4 border-t border-alpha-40 -translate-y-6">
        Based on the application score of {score}, we recommend
        {recommendationText}
      </p>

      <RecommendationButton
        recommendation={recommendation!}
        onClickMakeDecision={onButtonClick!}
        canMakeDecision={canMakeDecision}
      />
    </Card>
  );
};
